import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import StatusTable from './StatusTable';
import AlertsPanel from './AlertsPanel';
import SummaryBar from './SummaryBar';
import AlertPopup from './AlertPopup';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';

const Dashboard = ({ sites, metrics, alerts, summary, error, onRefresh, onAlertsUpdate }) => {
  const navigate = useNavigate();
  const { logout, authData } = useAuth();
  const { isConnected } = useWebSocket();
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // null, 'active', 'critical', 'warning', 'issues'

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Combine sites with their metrics
  const sitesWithMetrics = sites.map(site => {
    const siteMetrics = metrics.find(m => m.siteId === site.id) || {};
    return {
      ...site,
      ...siteMetrics,
      // Use server-calculated status if available, otherwise calculate client-side
      status: siteMetrics.status || getSiteStatus(siteMetrics, siteMetrics.lastUpdated)
    };
  });

  // Status priority for sorting (lower number = higher priority)
  const getStatusPriority = (status) => {
    switch (status) {
      case 'critical': return 1;
      case 'warning': return 2;
      case 'active': return 3;
      case 'not-monitored': return 4;
      default: return 5;
    }
  };

  // Filter sites based on search query and status filter
  const filteredSites = sitesWithMetrics.filter(site => {
    // Apply status filter
    if (statusFilter) {
      if (statusFilter === 'active') {
        // Active sites: status is 'active' AND no alerts
        const siteAlerts = alerts.filter(a => a.siteId === site.id && !a.dismissed);
        if (site.status !== 'active' || siteAlerts.length > 0) {
          return false;
        }
      } else if (statusFilter === 'critical') {
        // Sites with critical status
        if (site.status !== 'critical') {
          return false;
        }
      } else if (statusFilter === 'warning') {
        // Sites with warning status
        if (site.status !== 'warning') {
          return false;
        }
      } else if (statusFilter === 'issues') {
        // Sites with warning OR critical status
        if (site.status !== 'warning' && site.status !== 'critical') {
          return false;
        }
      }
    }
    
    // Apply search query filter
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const searchableFields = [
      String(site.id),
      site.name?.toLowerCase() || '',
      site.ip?.toLowerCase() || '',
      site.status?.toLowerCase() || ''
    ];
    
    return searchableFields.some(field => field.includes(query));
  });

  // Sort sites by status priority (Critical, Warning, Active, Not Monitored)
  const sortedSites = [...filteredSites].sort((a, b) => {
    const priorityA = getStatusPriority(a.status);
    const priorityB = getStatusPriority(b.status);
    if (priorityA !== priorityB) {
      return priorityA - priorityB; // Lower priority number = higher in list
    }
    // If same status, sort by site ID as secondary sort
    return a.id - b.id;
  });

  // Get active alerts (not dismissed) and sort by timestamp descending (newest first)
  // Note: Server already filters dismissed alerts, but we double-check here
  const activeAlerts = alerts
    .filter(a => {
      // Server should have already filtered dismissed alerts, but we check again for safety
      return !a.dismissed && (a.severity === 'critical' || a.severity === 'warning');
    })
    .sort((a, b) => {
      // Sort by timestamp descending (newest first)
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA; // Descending order
    });

  const handleSiteClick = (site) => {
    navigate(`/site/${site.id}`);
  };

  const handleAlertClick = (alert) => {
    setSelectedAlert(alert);
  };

  const handleAlertDismissed = async (alertId, dismissType) => {
    // Refresh alerts immediately to reflect the dismissal
    if (onAlertsUpdate) {
      // If there's a specific alerts update handler, use it
      await onAlertsUpdate();
    } else if (onRefresh) {
      // Otherwise, do a full refresh
      await onRefresh();
    }
  };

  const handleAlertResolved = async (alertId) => {
    // Refresh alerts immediately to reflect the resolution
    if (onAlertsUpdate) {
      // If there's a specific alerts update handler, use it
      await onAlertsUpdate();
    } else if (onRefresh) {
      // Otherwise, do a full refresh
      await onRefresh();
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-top">
          <div className="header-left">
            {authData && (authData.fullName || authData.userName) && (
              <div className="user-info">
                <span className="user-label">Logged in as:</span>
                <span className="user-name">{authData.fullName || authData.userName}</span>
              </div>
            )}
            <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
              <span className="status-dot"></span>
              <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
          </div>
          <div className="header-center">
            <img 
              src="/mypark-logo.png" 
              alt="MYPARK SMART PARKING" 
              className="dashboard-logo"
            />
          </div>
          <div className="header-right">
            <button 
              className="history-btn" 
              onClick={() => navigate('/alerts-history')} 
              title="View Alerts History"
            >
              📋 Alerts History
            </button>
            <button 
              className="settings-btn" 
              onClick={() => navigate('/alert-settings')} 
              title="View Alert Settings Overview"
            >
              ⚙️ Alert Settings Overview
            </button>
            <button className="refresh-btn" onClick={onRefresh} title="Refresh Sites">
              🔄 Refresh Sites
            </button>
            {authData && (
              <button 
                className="logout-btn" 
                onClick={handleLogout} 
                title={`Logout (${authData.userName || authData.fullName || 'User'})`}
              >
                🚪 Logout
              </button>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="dashboard-content">
        <div className="dashboard-left">
          <div className="panel">
            <div className="panel-header">
              <h2 className="panel-title">Sites Status</h2>
              <div className="panel-search">
                <div className="search-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by Site ID, Name, IP, or Status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="search-results-count">
                    {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} found
                  </div>
                )}
              </div>
            </div>
            <StatusTable 
              sites={sortedSites}
              onSelectSite={handleSiteClick}
            />
          </div>
        </div>

        <div className="dashboard-right">
          <div className="panel">
            <div className="panel-header">
              <h2 className="panel-title">Active Alerts</h2>
            </div>
            <AlertsPanel 
              alerts={activeAlerts} 
              onAlertClick={handleAlertClick}
              onAlertDismissed={handleAlertDismissed}
              onAlertResolved={handleAlertResolved}
            />
          </div>
        </div>
      </div>

      <SummaryBar 
        summary={summary} 
        onFilterClick={setStatusFilter}
        activeFilter={statusFilter}
      />

      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onDismissed={handleAlertDismissed}
        />
      )}
    </div>
  );
};

function getSiteStatus(metrics, lastUpdated) {
  // Check if there's no heartbeat data at all (not monitored)
  if (!metrics.heartbeat || metrics.heartbeat.status === undefined || metrics.heartbeat.status === null) {
    return 'not-monitored';
  }
  
  // Check for stale heartbeat (agent down) - if site had heartbeat but last update was > 3 minutes ago
  if (lastUpdated) {
    const now = new Date();
    const lastUpdateTime = new Date(lastUpdated);
    const timeSinceLastUpdate = now - lastUpdateTime;
    const THREE_MINUTES_MS = 3 * 60 * 1000;
    
    // If heartbeat existed before but hasn't been updated for 3+ minutes, agent is down
    if (timeSinceLastUpdate > THREE_MINUTES_MS) {
      return 'critical'; // Agent down
    }
  }
  
  if (metrics.heartbeat.status === 'offline') return 'critical';
  if (metrics.alerts && metrics.alerts.some(a => a.severity === 'critical')) return 'critical';
  if (metrics.alerts && metrics.alerts.some(a => a.severity === 'warning')) return 'warning';
  // Prefer server-calculated status; fallback uses rough defaults only when status missing
  if (metrics.cpu && metrics.cpu.usage > 80) return 'warning';
  if (metrics.memory && metrics.memory.used && metrics.memory.total) {
    const memoryPercent = (metrics.memory.used / metrics.memory.total) * 100;
    if (memoryPercent > 85) return 'warning';
  }
  const disk = metrics.diskSpace || metrics.disk;
  if (disk?.usagePercent > 90) return 'warning';
  return 'active';
}

export default Dashboard;

